import React from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import SVG from 'react-inlinesvg'
import {toAbsoluteUrl} from '../../../../template/_metronic/helpers'
import {InputPassword} from './input/InputPassword'
import {
  useForgotPasswordMutation,
  useRecoverPasswordMutation,
} from '../../../../redux/api/services/auth'
import {useAuth} from '../core/AuthProvider'
import {Navigate} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {RootState} from '../../../../redux/store'
import {CustomErrorAlert} from '../../../../libs/_fri/components/CustomErrorAlert/CustomErrorAlert'

const initialValues = {
  code: '',
  password: '',
  cPassword: '',
}

const resetPasswordSchema = Yup.object().shape({
  code: Yup.string()
    .required('Código es requerido')
    .matches(/^[A-Za-z0-9]+$/, 'El código no debe contener caracteres especiales')
    .min(6, 'El código debe tener 6 caracteres')
    .max(6, 'El código debe tener 6 caracteres'),
  password: Yup.string().required('Contraseña es requerida'),
  cPassword: Yup.string()
    .required('Contraseña es requerida')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: () => Yup.string().oneOf([Yup.ref('password')], 'Las contraseñas no coinciden'),
    }),
})

export function ResetPassword() {
  const [recoverPassword, {isError, isLoading, isSuccess, error, reset}] =
    useRecoverPasswordMutation()
  const [forgotPassword, {isSuccess: isSuccessForgotPassword}] = useForgotPasswordMutation()
  const {email} = useAuth()
  const infoState = useSelector((state: RootState) => state.info)

  const formik = useFormik({
    initialValues,
    validationSchema: resetPasswordSchema,
    onSubmit: (values) => {
      recoverPassword({
        info: infoState,
        requestContent: {
          email: email,
          verificationCode: values.code,
          password: values.cPassword,
        },
      })
    },
  })

  return (
    <>
      {isSuccess && <Navigate to='/auth' />}
      {!isSuccess && (
        <form
          className='form w-100 py-10 py-lg-11'
          noValidate
          id='kt_login_password_reset_form'
          onSubmit={formik.handleSubmit}
        >
          <div className='d-flex flex-column align-items-center mb-10 text-center'>
            <SVG src={toAbsoluteUrl('/media/soyfri-media/logo-planillas.svg')} className='mb-8' />
            <SVG src={toAbsoluteUrl('/media/soyfri-media/login-separator.svg')} className='mb-7' />
            <h1 className='text-primary-dark fs-2hx'>Crear una contraseña</h1>
            <span className='text-gray-500 fs-3 px-5'>Te enviamos un código a tu correo</span>
            <span className='text-primary fs-3'>{email}</span>
          </div>

          <CustomErrorAlert show={isError} error={error} close={reset} />
          {isSuccessForgotPassword && (
            <div className='mb-lg-15 alert alert-success'>
              <div className='alert-text font-weight-bold'>
                Se reenvío el código a tu correo nuevamente
              </div>
            </div>
          )}

          {/* begin::Form group */}
          <div className='fv-row mb-8'>
            <label className='form-label fw-semibold text-primary-dark fs-3'>Código</label>
            <input
              type='text'
              placeholder='Código de verificación'
              autoComplete='off'
              {...formik.getFieldProps('code')}
              className={clsx(
                'form-control form-control-solid text-primary-dark border-0 fs-3',
                {'is-invalid': formik.touched.code && formik.errors.code},
                {
                  'is-valid': formik.touched.code && !formik.errors.code,
                }
              )}
            />

            {formik.touched.code && formik.errors.code ? (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.code}</span>
                </div>
              </div>
            ) : (
              <a
                className='form-text text-primary fw-semibold fst-italic fs-4'
                href='#'
                onClick={() => {
                  forgotPassword({info: infoState, requestContent: {email: email}})
                }}
              >
                Reenviar código
              </a>
            )}
          </div>
          {/* end::Form group */}

          <div className='fv-row mb-8'>
            <label className='form-label fw-semibold text-primary-dark fs-3'>Contraseña</label>
            <InputPassword {...formik.getFieldProps('password')} className={''} />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>

          <div className='fv-row mb-8'>
            <label className='form-label fw-semibold text-primary-dark fs-3'>
              Confirmar contraseña
            </label>
            <InputPassword {...formik.getFieldProps('cPassword')} className={''} />
            {formik.touched.cPassword && formik.errors.cPassword && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.cPassword}</span>
                </div>
              </div>
            )}
          </div>

          {/* begin::Form group */}
          <div className='d-grid'>
            <button
              type='submit'
              id='kt_password_reset_submit'
              className='btn btn-lg btn-primary rounded-pill'
            >
              <span className='indicator-label'>Siguiente</span>
              {isLoading && (
                <span className='indicator-progress'>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
          {/* end::Form group */}
        </form>
      )}
    </>
  )
}
